import React, { FC, useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { 
  getStoreClosureDates, 
  addStoreClosureDate, 
  updateStoreClosureDate, 
  deleteStoreClosureDate 
} from "../../../redux/actions/storeClosureDateActions";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { CustomButton } from "../../atoms";
import { Colors } from "../../../configs";
import dayjs from "dayjs";
import CustomDateTimePicker from "../../atoms/custom-date-time-picker/custom-date-time-picker";
import { IStoreClosureDateResponse } from "../../../typings/interfaces/responses/store-closure-date-response";

interface IStoreClosureDatesProps {
  storeId: number;
  merchantId: number;
}

interface IAddEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { closureDate: string; reason?: string }) => void;
  loading: boolean;
  editData?: IStoreClosureDateResponse;
}

const AddEditDialog: FC<IAddEditDialogProps> = ({ 
  open, 
  onClose, 
  onSave, 
  loading, 
  editData 
}) => {
  const [closureDate, setClosureDate] = useState<dayjs.Dayjs | null>(
    editData ? dayjs(editData.closureDate) : null
  );
  const [reason, setReason] = useState(editData?.reason || "");

  useEffect(() => {
    if (editData) {
      setClosureDate(dayjs(editData.closureDate));
      setReason(editData.reason || "");
    } else {
      setClosureDate(null);
      setReason("");
    }
  }, [editData, open]);

  const handleSave = () => {
    if (!closureDate) return;
    
    onSave({
      closureDate: closureDate.format("YYYY-MM-DD"),
      reason: reason.trim() || undefined,
    });
  };

  const handleClose = () => {
    setClosureDate(null);
    setReason("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editData ? "Edit Closure Date" : "Add Closure Date"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Closure Date *
            </Typography>
            <CustomDateTimePicker
              type="date"
              value={closureDate}
              onChange={(date) => setClosureDate(date)}
              enableSelect="future"
              sx={{ width: "100%" }}
            />
          </Box>
          <TextField
            label="Reason (Optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={2}
            placeholder="e.g., Christmas Day, Maintenance, etc."
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleClose} variant="outlined">
          Cancel
        </CustomButton>
        <CustomButton 
          onClick={handleSave} 
          disabled={!closureDate || loading}
        >
          {loading ? "Loading..." : (editData ? "Update" : "Add")} Closure Date
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export const StoreClosureDates: FC<IStoreClosureDatesProps> = ({ 
  storeId, 
  merchantId 
}) => {
  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbarContext();
  const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<IStoreClosureDateResponse | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const {
    closureDates,
    closureDatesLoading,
    closureDatesError,
    addClosureDateLoading,
    updateClosureDateLoading,
    deleteClosureDateLoading,
  } = useAppSelector((state) => state.storeClosureDate);

  useEffect(() => {
    if (storeId && merchantId) {
      dispatch(getStoreClosureDates({ storeId, merchantId }));
    }
  }, [dispatch, storeId, merchantId]);

  useEffect(() => {
    if (closureDatesError) {
      showSnackbar(`Error loading closure dates: ${closureDatesError}`, "error");
    }
  }, [closureDatesError, showSnackbar]);

  const handleAdd = () => {
    setEditData(undefined);
    setAddEditDialogOpen(true);
  };

  const handleEdit = (closureDate: IStoreClosureDateResponse) => {
    setEditData(closureDate);
    setAddEditDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleSave = async (data: { closureDate: string; reason?: string }) => {
    try {
      if (editData) {
        // Update existing
        await dispatch(updateStoreClosureDate({
          id: editData.id,
          data: {
            storeId,
            merchantId,
            closureDate: data.closureDate,
            reason: data.reason,
          },
        })).unwrap();
        showSnackbar("Closure date updated successfully", "success");
      } else {
        // Add new
        await dispatch(addStoreClosureDate({
          storeId,
          merchantId,
          closureDate: data.closureDate,
          reason: data.reason,
        })).unwrap();
        showSnackbar("Closure date added successfully", "success");
      }
      
      setAddEditDialogOpen(false);
      // Refresh the list
      dispatch(getStoreClosureDates({ storeId, merchantId }));
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to save closure date", "error");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      await dispatch(deleteStoreClosureDate(deleteId)).unwrap();
      showSnackbar("Closure date deleted successfully", "success");
      setDeleteConfirmOpen(false);
      setDeleteId(null);
      // Refresh the list
      dispatch(getStoreClosureDates({ storeId, merchantId }));
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to delete closure date", "error");
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteId(null);
  };

  const sortedClosureDates = closureDates?.body ? 
    [...closureDates.body].sort((a, b) => 
      dayjs(a.closureDate).valueOf() - dayjs(b.closureDate).valueOf()
    ) : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon sx={{ color: Colors.PRIMARY }} />
          <Typography variant="h6" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Store Closure Dates
          </Typography>
        </Box>
        <CustomButton
          onClick={handleAdd}
          startIcon={<AddIcon />}
          sx={{ minWidth: "auto" }}
        >
          Add Closure Date
        </CustomButton>
      </Box>

      <Typography variant="body2" sx={{ color: Colors.MAIN_GRAY }}>
        Configure specific dates when the store will be closed (holidays, maintenance, etc.)
      </Typography>

      {closureDatesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${Colors.ACCENT_GRAY_DISABLED}`, borderRadius: 1 }}>
          {sortedClosureDates.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: Colors.MAIN_GRAY }}>
                No closure dates configured
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedClosureDates.map((closureDate) => (
                  <TableRow key={closureDate.id}>
                    <TableCell>
                      {dayjs(closureDate.closureDate).format("MMM DD, YYYY")}
                    </TableCell>
                    <TableCell>
                      {closureDate.reason || (
                        <Typography variant="body2" sx={{ color: Colors.MAIN_GRAY, fontStyle: "italic" }}>
                          No reason provided
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(closureDate)}
                          sx={{ color: Colors.PRIMARY }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(closureDate.id)}
                          sx={{ color: Colors.ERROR_RED }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      <AddEditDialog
        open={addEditDialogOpen}
        onClose={() => setAddEditDialogOpen(false)}
        onSave={handleSave}
        loading={addClosureDateLoading || updateClosureDateLoading}
        editData={editData}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Closure Date</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this closure date? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleCancelDelete} variant="outlined">
            Cancel
          </CustomButton>
          <CustomButton 
            onClick={handleConfirmDelete}
            disabled={deleteClosureDateLoading}
            sx={{ backgroundColor: Colors.ERROR_RED, "&:hover": { backgroundColor: Colors.ERROR_RED } }}
          >
            {deleteClosureDateLoading ? "Deleting..." : "Delete"}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
